import { Button, Card, CardHeader, CardPreview, makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { bundleIcon, Delete20Filled, Delete20Regular, Edit20Filled, Edit20Regular, Warning20Filled } from '@fluentui/react-icons';
import { CSSProperties, forwardRef, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { customPalette } from '../../theme';

export interface IAddReturnCardProps {
    taxReturnItemName: string;
    calculationKey: number;
    calculationName: string;
    calculationDescription: string;
    entityKey: number;
    jurisdictionCode: string;
    caseCode: string;
    taxYearName: string;
    onEditReturn: () => void;
    onDeleteReturn: () => void;
    isDuplicated: boolean;
}

const EditIcon = bundleIcon(Edit20Filled, Edit20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);

const useStyles = makeStyles({
    // CardPreview forces direct children to display:block; nest the flex row inside a full-width wrapper.
    contentWrapper: {
        width: '100%',
    },
    contentRow: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        width: '100%',
        gap: '12px',
    },
    fieldColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
    calculationField: {
        flexGrow: 1,
        flexShrink: 1,
    },
    metaFields: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        flexGrow: 0,
        flexShrink: 0,
        marginLeft: 'auto',
        gap: '12px',
    },
    cardStyles: {
        padding: '0px 12px 12px 12px',
    },
    cardHeaderContainerStyles: {
        display: 'flex',
        flexGrow: 1,
        alignItems: 'start',
        justifyContent: 'space-between',
        width: '100%',
    },
    cardHeaderTextStyles: {
        fontSize: '14px',
        fontWeight: 600,
    },
    cardContentTextStyles: {
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 400,
    },
    lightTextStyles: {
        color: tokens.colorNeutralForeground3,
    },
    cardTitleContainerStyles: { display: 'flex', gap: '20px' },
    headerActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
    },
    // rgb(50, 49, 48) — match icon tab / action bar rest color (not subtle muted)
    actionButton: {
        color: customPalette.blueDark,
        ':hover': {
            color: customPalette.blueDark,
        },
        '> .fui-Button__icon': {
            color: 'inherit',
        },
    },
    warningIcon: {
        color: tokens.colorStatusWarningForeground1,
        ':focus': {
            border: '2px solid black',
            borderRadius: '5px',
        },
    },
});

const AddReturnCard = forwardRef(
    (
        {
            taxReturnItemName,
            calculationKey,
            calculationName,
            calculationDescription,
            entityKey,
            jurisdictionCode,
            caseCode,
            taxYearName,
            onEditReturn,
            onDeleteReturn,
            isDuplicated,
        }: IAddReturnCardProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const { t } = useTranslation();
        const {
            contentWrapper,
            contentRow,
            fieldColumn,
            calculationField,
            metaFields,
            cardStyles,
            cardHeaderContainerStyles,
            cardHeaderTextStyles,
            cardContentTextStyles,
            lightTextStyles,
            cardTitleContainerStyles,
            headerActions,
            actionButton,
            warningIcon,
        } = useStyles();

        const handleKeyDown = (event: any) => {
            const duplicatedCalcWarning = document.getElementById(`warning-icon-${calculationKey}-${entityKey}`);
            const buttons = Array.from(document.getElementsByName(`card-button-${calculationKey}-${entityKey}`));

            if (duplicatedCalcWarning) {
                buttons.unshift(duplicatedCalcWarning);
            }

            let currentIndex = -1;

            buttons.forEach((element, index) => {
                if (element === document.activeElement) {
                    currentIndex = index;
                }
            });

            if (event.key === 'ArrowRight') {
                const nextIndex = (currentIndex + 1) % buttons.length;
                buttons[nextIndex].focus();
                event.preventDefault();
            }

            if (event.key === 'ArrowLeft') {
                const nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
                buttons[nextIndex].focus();
                event.preventDefault();
            }
        };

        const cardStyle: CSSProperties = {
            backgroundColor: isDuplicated ? customPalette.warningBackground : 'white',
        };

        return (
            <Card ref={ref} tabIndex={-1} data-testid={`return-card-${calculationName}`} onKeyDown={handleKeyDown} style={cardStyle}>
                <CardHeader
                    header={
                        <div className={cardHeaderContainerStyles}>
                            <div className={cardTitleContainerStyles}>
                                {isDuplicated && (
                                    <Tooltip
                                        content={t('duplicatedCalculationWarning').toString()}
                                        relationship='description'
                                        appearance='inverted'
                                        withArrow
                                        positioning='above'
                                    >
                                        <Warning20Filled
                                            tabIndex={-1}
                                            aria-hidden={false}
                                            id={`warning-icon-${calculationKey}-${entityKey}`}
                                            data-testid='duplicated-return-icon'
                                            className={warningIcon}
                                        />
                                    </Tooltip>
                                )}
                                <h3 className={cardHeaderTextStyles}>{taxReturnItemName}</h3>
                            </div>
                            <div className={headerActions}>
                                <Tooltip
                                    content={t('editCalculation').toString()}
                                    relationship='description'
                                    appearance='inverted'
                                    withArrow
                                    positioning='above'
                                >
                                    <Button
                                        size='small'
                                        appearance='subtle'
                                        className={actionButton}
                                        icon={<EditIcon />}
                                        aria-label={t('editCalculation').toString()}
                                        tabIndex={-1}
                                        data-testid='edit-calculation-icon'
                                        name={`card-button-${calculationKey}-${entityKey}`}
                                        onClick={onEditReturn}
                                    />
                                </Tooltip>

                                <Tooltip
                                    content={t('deleteCalculation').toString()}
                                    relationship='description'
                                    appearance='inverted'
                                    withArrow
                                    positioning='above'
                                >
                                    <Button
                                        size='small'
                                        appearance='subtle'
                                        className={actionButton}
                                        icon={<DeleteIcon />}
                                        aria-label={t('deleteCalculation').toString()}
                                        tabIndex={-1}
                                        data-testid='remove-calculation-icon'
                                        name={`card-button-${calculationKey}-${entityKey}`}
                                        onClick={onDeleteReturn}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    }
                ></CardHeader>
                <CardPreview className={cardStyles}>
                    <div className={contentWrapper}>
                        <div className={contentRow}>
                            <div className={mergeClasses(fieldColumn, calculationField)}>
                                <label className={mergeClasses(cardContentTextStyles, lightTextStyles)}>{t('formOrCalculation')}</label>
                                <label className={cardContentTextStyles}>{calculationDescription}</label>
                            </div>
                            <div className={metaFields}>
                                <div className={fieldColumn}>
                                    <label className={mergeClasses(cardContentTextStyles, lightTextStyles)}>{t('jurisdiction')}</label>
                                    <label className={cardContentTextStyles}>{jurisdictionCode}</label>
                                </div>
                                <div className={fieldColumn}>
                                    <label className={mergeClasses(cardContentTextStyles, lightTextStyles)}>{t('context')}</label>
                                    <label className={cardContentTextStyles}>
                                        {caseCode}, {taxYearName}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardPreview>
            </Card>
        );
    }
);

export default AddReturnCard;
